import type { SwatchKind } from "@/components/primitives";

export const FIG_ITEM = {
  id: "FIG-00241",
  title: "Faded Wool Chore Coat",
  brand: "Sears Roebuck & Co.",
  era: "c. 1972–1978",
  origin: "Made in USA",
  category: "Outerwear / Workwear",
  material: "100% Wool, cotton-twill lining",
  size: "M (labeled 40)",
  color: "Umber / faded",
  condition: "Very Good",
  conditionNotes: "Minor moth nibble on left cuff. Collar wear consistent with age.",
  verdict: "PASS" as "PASS" | "FAIL",
  verdictLabel: "VINTAGE CERTIFIED",
  confidence: 94,
  rarity: "Uncommon",
  tags: ["workwear", "wool", "70s", "chore coat", "sears"],
  notes: [
    "Single-needle chainstitch at hem — consistent with pre-1980 manufacture.",
    "Union tag (ILGWU blue-white-red) — issued 1974–1995.",
    'Metal shank buttons, stamped "SEARS" — matches 70s catalog.',
    "Care label pre-ISO symbology; US only. Pre-1985.",
  ],
  forensic: [
    { label: "Main label", value: "Sears Roebuck / The Men's Store", confidence: 0.98 },
    { label: "Union tag", value: "ILGWU (1974–1995)", confidence: 0.91 },
    { label: "Care label", value: "Pre-ISO symbol set", confidence: 0.88 },
    { label: "Seam construction", value: "Chainstitched hem", confidence: 0.93 },
    { label: "Button hardware", value: "Stamped brass shank", confidence: 0.87 },
    { label: "Fabric analysis", value: "100% wool, ~14oz", confidence: 0.79 },
  ],
  verification: null as null | {
    category_flag: "fur" | "leather" | "luxury" | "exotic";
    tier: "photo_verified" | "self_reported" | "uncertain";
    evidence: string[];
    prompts: string[];
  },
  pricing: {
    low: 58,
    median: 92,
    high: 145,
    suggested: 98,
    suggestedConfidence: "HIGH",
    trend: [62, 71, 68, 82, 79, 85, 92, 88, 95, 98, 92, 102],
    platforms: [
      { name: "Depop", price: 88, note: "Best for 20–30 crowd", fee: 10, net: 79, hot: true },
      { name: "Grailed", price: 112, note: "Premium ask, slower", fee: 9, net: 102 },
      { name: "eBay", price: 95, note: "Widest audience", fee: 13, net: 83 },
      { name: "Etsy", price: 105, note: "Vintage-focused", fee: 8, net: 97 },
    ] as { name: string; price: number; note: string; fee: number; net: number; hot?: boolean }[],
    comps: [
      { platform: "Depop", title: "70s Sears wool chore M", price: 94, days: 3, img: "olive" as SwatchKind },
      { platform: "eBay", title: "Vtg Sears Wool Jacket 40", price: 86, days: 8, img: "rust" as SwatchKind },
      { platform: "Grailed", title: "Sears 1970s workwear chore", price: 128, days: 14, img: "ink" as SwatchKind },
      { platform: "Depop", title: "Brown wool 70s chore coat", price: 75, days: 5, img: "cream" as SwatchKind },
      { platform: "Etsy", title: "Vintage Sears wool coat 70s", price: 110, days: 11, img: "stripes" as SwatchKind },
    ],
  },
};

export interface ClosetItem {
  id: number;
  title: string;
  brand: string;
  era: string;
  price: number;
  verdict: "PASS" | "FAIL";
  img: SwatchKind;
  status: string;
}

export const FIG_CLOSET: ClosetItem[] = [
  { id: 1, title: "Faded Wool Chore Coat", brand: "Sears", era: "70s", price: 98, verdict: "PASS", img: "olive", status: "Listed · Depop" },
  { id: 2, title: "Paper-Thin Band Tee", brand: "Hanes", era: "80s", price: 42, verdict: "PASS", img: "ink", status: "Kept" },
  { id: 3, title: "Acid Wash Denim", brand: "Levi's 501", era: "90s", price: 65, verdict: "PASS", img: "cream", status: "Sold · $68" },
  { id: 4, title: "Silk Scarf, Floral", brand: "Liberty of London", era: "60s", price: 54, verdict: "PASS", img: "rust", status: "Listed · Etsy" },
  { id: 5, title: "Cotton Crewneck", brand: "Shein", era: "2023", price: 4, verdict: "FAIL", img: "stripes", status: "Skipped" },
  { id: 6, title: "Pendleton Plaid Shirt", brand: "Pendleton", era: "80s", price: 72, verdict: "PASS", img: "olive", status: "Kept" },
];

export const FIG_HISTORY = [
  { id: "A", date: "Apr 12", title: "Levi's 501 Denim", listed: 65, sold: 68, platform: "Depop", days: 4, img: "cream" as SwatchKind },
  { id: "B", date: "Mar 28", title: "Ralph Lauren Oxford", listed: 34, sold: 32, platform: "eBay", days: 11, img: "stripes" as SwatchKind },
  { id: "C", date: "Mar 14", title: "Carhartt Barn Jacket", listed: 110, sold: 118, platform: "Grailed", days: 6, img: "rust" as SwatchKind },
  { id: "D", date: "Feb 22", title: "Champion Reverse Weave", listed: 55, sold: 52, platform: "Depop", days: 9, img: "ink" as SwatchKind },
];

export const FIG_GUESSES = [
  "reading the care label…",
  "is that a chainstitch?",
  "counting buttons…",
  "cross-referencing 1974 catalogs…",
  "checking ILGWU records…",
  "consulting the seam gods…",
  "smelling for mothballs…",
  "measuring fabric weight…",
  "sniffing out the decade…",
  "comparing 147 recent sales…",
];
